import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import { StackCards } from '@/components/StackCards';
import { Card } from '@/components/StackCards/types';
import IconDeferredPayment from '@/assets/icons/heroIcons/Deferred-payment.svg';
import IconThird from '@/assets/icons/heroIcons/Icon-third.svg';
import IconMaxi from '@/assets/icons/heroIcons/Maxi.svg';
import IconCard from '@/assets/icons/heroIcons/Card-front.svg';
import Hero from '@/sections/Hero';
import MoreInfoSection from '@/sections/MoreInfo';
import { FaqIntro } from '@/sections/FaqIntro';
import { Button } from '@/components/buttons';
import { SectionHeadline } from '@/components/SectionHeadline';
import { BenefitTiles } from '@/components/tiles';
import { ContentContainer } from '@/components/layout';
import { type IntlProps, getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { Trans, useTranslation } from 'react-i18next';
import { LINKS } from '@/constants/links';
import React from 'react';
import { mergeClasses } from '@/utils/style';
import { NumericRange } from '@/types/range';
import Image from 'next/image';
import IconCheck from '@/assets/icons/heroIcons/Check.svg';
import { Link } from '@/components/links';
import Floating from '@/sections/Floating';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { SeoHeader } from '@/components/SeoHeader';
import { Text } from '@/components/typography';
import { AnimatedSection, AnimatedContent } from '@/components/animation';
import { isAllowedRoute } from '@/lang/pagesConfig';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { Calculator } from '@/sections/insuranceAndWarranty/Calculator';
import { getSectionAnchorId } from '@/utils/anchors';
import { useProductRegistrationButtons, useProductRegistrationLink } from '@/hooks/useProductRegistrationLink';
interface Props extends IntlProps {
  menuPromoBanners?: PageMenuPromoBanner[];
  pageBanner: PageBanner | null;
}
export default function Page(props) {
  const $ = _c(201);
  usePageView("insuranceAndWarranty");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ["insuranceAndWarranty", "core"];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation(t0);
  const {
    pageBanner,
    menuPromoBanners
  } = props;
  const registrationLink = useProductRegistrationLink("pojisteni");
  const heroButtons = useProductRegistrationButtons(pageBanner?.buttons, "pojisteni");
  let t1;
  if ($[1] !== registrationLink || $[2] !== t) {
    t1 = Array.from({
      length: 5
    }, (_, index) => {
      const cardIndex = index + 1 as NumericRange<1, 5>;
      return {
        index,
        menuTitle: "",
        title: <div className={mergeClasses(cardIndex === 4 && "text-center lg:text-left")}>{t(`howItWorks.items.${cardIndex}.title`)}</div>,
        ...(cardIndex < 5 ? {
          description: <Trans t={t} i18nKey={`howItWorks.items.${cardIndex}.text`}><Link to={registrationLink} testId="insuranceRegistration" analyticsParam="insuranceRegistration" /><Link to={LINKS().insuranceReport} testId="insuranceReport" analyticsParam="insuranceReport" /></Trans>
        } : {
          footer: <Button analyticsParam="insuranceHowItWorksiWantMaxi" testId="insuranceHowItWorksiWantMaxi" variant="primary" size="lg" className="m-auto w-full md:w-auto lg:m-0" to={registrationLink}>{t(`howItWorks.items.${cardIndex}.text`)}</Button>
        }),
        images: {
          desktop: {
            src: `/images/sections/insuranceAndWarranty/cards/desktop/${process.env.NEXT_PUBLIC_LANGUAGE}/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.alt`)
          },
          mobile: {
            src: `/images/sections/insuranceAndWarranty/cards/mobile/${process.env.NEXT_PUBLIC_LANGUAGE}/${cardIndex}.png`,
            alt: t(`howItWorks.items.${cardIndex}.alt`)
          },
          ...(cardIndex < 5 && {
            variant: "appMockup"
          })
        }
      };
    });
    $[1] = registrationLink;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const cards = t1;
  let t2;
  if ($[4] !== t) {
    t2 = t("seo.title");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = t("seo.description");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <SeoHeader title={t2} description={t3} />;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const t5 = pageBanner?.videoId;
  let t6;
  if ($[11] !== pageBanner?.title || $[12] !== t) {
    t6 = pageBanner?.title ?? t("banner.title");
    $[11] = pageBanner?.title;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== pageBanner?.text || $[15] !== t) {
    t7 = pageBanner?.text ?? t("banner.text");
    $[14] = pageBanner?.text;
    $[15] = t;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== heroButtons || $[18] !== registrationLink || $[19] !== t) {
    t8 = heroButtons ?? [{
      ariaLabel: t("banner.cta"),
      label: t("banner.cta"),
      to: registrationLink,
      testId: "insuranceHeroPrimaryCTA",
      analyticsParam: "insuranceHeroPrimaryCTA",
      variant: "primary"
    }];
    $[17] = heroButtons;
    $[18] = registrationLink;
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = pageBanner?.image?.url ?? "/images/sections/insuranceAndWarranty/hero/cs/hero.png";
  let t10;
  if ($[21] !== pageBanner?.image?.alt || $[22] !== t) {
    t10 = pageBanner?.image?.alt ?? t("banner.title");
    $[21] = pageBanner?.image?.alt;
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t9) {
    t11 = {
      url: t9,
      alt: t10
    };
    $[24] = t10;
    $[25] = t9;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const t12 = pageBanner?.variant || "mockupMobile";
  let t13;
  if ($[27] !== t11 || $[28] !== t12 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7 || $[32] !== t8) {
    t13 = <Hero videoId={t5} title={t6} text={t7} buttons={t8} image={t11} variant={t12} />;
    $[27] = t11;
    $[28] = t12;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t8;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = getSectionAnchorId("howWeProtect");
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t) {
    t15 = t("howWeProtect.items.1.text");
    $[35] = t;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  let t16;
  if ($[37] !== t) {
    t16 = t("howWeProtect.items.1.title");
    $[37] = t;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t) {
    t17 = t("howWeProtect.items.1.alt");
    $[39] = t;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== t17) {
    t18 = {
      alt: t17,
      src: "/images/sections/insuranceAndWarranty/boxes/1.png"
    };
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t15 || $[44] !== t16 || $[45] !== t18) {
    t19 = {
      anchorKey: "howWeProtect.items.1.title",
      variant: "small",
      content: t15,
      title: t16,
      image: t18
    };
    $[43] = t15;
    $[44] = t16;
    $[45] = t18;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== t) {
    t20 = t("howWeProtect.items.2.text");
    $[47] = t;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  let t21;
  if ($[49] !== t) {
    t21 = t("howWeProtect.items.2.title");
    $[49] = t;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t) {
    t22 = t("howWeProtect.items.2.alt");
    $[51] = t;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t22) {
    t23 = {
      alt: t22,
      src: "/images/sections/insuranceAndWarranty/boxes/2.png"
    };
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== t20 || $[56] !== t21 || $[57] !== t23) {
    t24 = {
      anchorKey: "howWeProtect.items.2.title",
      variant: "small",
      content: t20,
      title: t21,
      image: t23,
      imagePosition: "right"
    };
    $[55] = t20;
    $[56] = t21;
    $[57] = t23;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t) {
    t25 = t("howWeProtect.items.3.text");
    $[59] = t;
    $[60] = t25;
  } else {
    t25 = $[60];
  }
  let t26;
  if ($[61] !== t) {
    t26 = t("howWeProtect.items.3.title");
    $[61] = t;
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] !== t) {
    t27 = t("howWeProtect.items.3.alt");
    $[63] = t;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  let t28;
  if ($[65] !== t27) {
    t28 = {
      alt: t27,
      src: "/images/sections/insuranceAndWarranty/boxes/3.png"
    };
    $[65] = t27;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== t25 || $[68] !== t26 || $[69] !== t28) {
    t29 = {
      anchorKey: "howWeProtect.items.3.title",
      variant: "small",
      content: t25,
      title: t26,
      image: t28
    };
    $[67] = t25;
    $[68] = t26;
    $[69] = t28;
    $[70] = t29;
  } else {
    t29 = $[70];
  }
  let t30;
  if ($[71] !== t19 || $[72] !== t24 || $[73] !== t29) {
    t30 = [t19, t24, t29];
    $[71] = t19;
    $[72] = t24;
    $[73] = t29;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== t) {
    t31 = t("howWeProtect.cta");
    $[75] = t;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== registrationLink || $[78] !== t31) {
    t32 = {
      analyticsParam: "insuranceRegistration",
      testId: "insuranceRegistration",
      text: t31,
      to: registrationLink
    };
    $[77] = registrationLink;
    $[78] = t31;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== t) {
    t33 = t("howWeProtect.title");
    $[80] = t;
    $[81] = t33;
  } else {
    t33 = $[81];
  }
  let t34;
  if ($[82] !== t30 || $[83] !== t32 || $[84] !== t33) {
    t34 = <AnimatedSection bgColor="bg-blue-100" id={t14}><AnimatedContent className="relative z-30 pb-14 lg:pb-20"><Floating boxes={t30} button={t32} title={t33} /></AnimatedContent></AnimatedSection>;
    $[82] = t30;
    $[83] = t32;
    $[84] = t33;
    $[85] = t34;
  } else {
    t34 = $[85];
  }
  let t35;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = getSectionAnchorId("calculator");
    $[86] = t35;
  } else {
    t35 = $[86];
  }
  let t36;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <AnimatedSection bgColor="bg-blue-700" id={t35}><div id="calculator" className="scroll-mt-20"><AnimatedContent className="overflow-x-hidden py-10 lg:py-20"><Calculator /></AnimatedContent></div></AnimatedSection>;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = getSectionAnchorId("howItWorks.title");
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] !== t) {
    t38 = t("howItWorks.title");
    $[89] = t;
    $[90] = t38;
  } else {
    t38 = $[90];
  }
  let t39;
  if ($[91] !== t38) {
    t39 = <SectionHeadline className="mb-12 px-4 md:mb-16" anchorKey="howItWorks.title">{t38}</SectionHeadline>;
    $[91] = t38;
    $[92] = t39;
  } else {
    t39 = $[92];
  }
  let t40;
  if ($[93] !== cards) {
    t40 = <StackCards imagePosition="left" cards={cards} />;
    $[93] = cards;
    $[94] = t40;
  } else {
    t40 = $[94];
  }
  let t41;
  if ($[95] !== t39 || $[96] !== t40) {
    t41 = <AnimatedSection bgColor="bg-blue-100" id={t37}><div id="how-it-works" className="scroll-mt-20"><AnimatedContent className="relative pt-10 pb-14 lg:pt-20 lg:pb-32">{t39}{t40}</AnimatedContent></div></AnimatedSection>;
    $[95] = t39;
    $[96] = t40;
    $[97] = t41;
  } else {
    t41 = $[97];
  }
  let t42;
  if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = getSectionAnchorId("fairRules");
    $[98] = t42;
  } else {
    t42 = $[98];
  }
  let t43;
  if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
    t43 = <div className="bgCircle absolute right-0 bottom-0 hidden translate-x-1/2 -translate-y-1/4 rotate-90 lg:block" />;
    $[99] = t43;
  } else {
    t43 = $[99];
  }
  let t44;
  if ($[100] !== t) {
    t44 = t("fairRules.title");
    $[100] = t;
    $[101] = t44;
  } else {
    t44 = $[101];
  }
  let t45;
  if ($[102] !== t44) {
    t45 = <div className="relative z-10 mx-auto w-full max-w-[61rem] text-center"><SectionHeadline className="mb-4" anchorKey="fairRules.title">{t44}</SectionHeadline></div>;
    $[102] = t44;
    $[103] = t45;
  } else {
    t45 = $[103];
  }
  let t46;
  if ($[104] !== t) {
    t46 = <div className="mb-10 flex flex-col gap-4 md:mb-12"><BenefitTiles classNameItem="md:h-[7.5rem]" items={Array.from({
        length: 6
      }, (__0, index_0) => {
        const itemIndex = index_0 + 1 as NumericRange<1, 6>;
        return {
          title: t(`fairRules.items.${itemIndex}`)
        };
      })} /></div>;
    $[104] = t;
    $[105] = t46;
  } else {
    t46 = $[105];
  }
  let t47;
  if ($[106] !== t) {
    t47 = t("fairRules.cta");
    $[106] = t;
    $[107] = t47;
  } else {
    t47 = $[107];
  }
  let t48;
  if ($[108] !== registrationLink || $[109] !== t47) {
    t48 = <Button to={registrationLink} testId="insuranceRevolving" analyticsParam="insuranceRevolving" size="lg" variant="primary" align="center">{t47}</Button>;
    $[108] = registrationLink;
    $[109] = t47;
    $[110] = t48;
  } else {
    t48 = $[110];
  }
  let t49;
  if ($[111] !== t46 || $[112] !== t48) {
    t49 = <div className="relativ z-10 mx-auto mt-8 max-w-[61rem] md:mt-16">{t46}{t48}</div>;
    $[111] = t46;
    $[112] = t48;
    $[113] = t49;
  } else {
    t49 = $[113];
  }
  let t50;
  if ($[114] !== t45 || $[115] !== t49) {
    t50 = <AnimatedContent className="relative z-30 px-4 pb-10 lg:pb-20"><div className="relative overflow-hidden">{t43}{t45}{t49}</div></AnimatedContent>;
    $[114] = t45;
    $[115] = t49;
    $[116] = t50;
  } else {
    t50 = $[116];
  }
  let t51;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t51 = <div className="bgCircle absolute top-0 left-0 hidden h-[24rem] w-[24rem] translate-x-1/4 translate-y-20 rotate-90 lg:block" />;
    $[117] = t51;
  } else {
    t51 = $[117];
  }
  let t52;
  if ($[118] !== t) {
    t52 = t("examples.title");
    $[118] = t;
    $[119] = t52;
  } else {
    t52 = $[119];
  }
  let t53;
  if ($[120] !== t52) {
    t53 = <SectionHeadline className="mb-5" anchorKey="examples.title">{t52}</SectionHeadline>;
    $[120] = t52;
    $[121] = t53;
  } else {
    t53 = $[121];
  }
  let t54;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t54 = <Link to={`${LINKS().conditions}#insurance`} testId="consitions" analyticsParam="consitions" />;
    $[122] = t54;
  } else {
    t54 = $[122];
  }
  let t55;
  if ($[123] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <Link to={LINKS().insuranceReport} testId="insuranceReport" analyticsParam="insuranceReport" />;
    $[123] = t55;
  } else {
    t55 = $[123];
  }
  let t56;
  if ($[124] !== t) {
    t56 = <Text variant="p2" className="mb-12 text-center text-gray-800 lg:mb-16"><Trans t={t} i18nKey="examples.text">{t54}{t55}</Trans></Text>;
    $[124] = t;
    $[125] = t56;
  } else {
    t56 = $[125];
  }
  let t57;
  if ($[126] !== t53 || $[127] !== t56) {
    t57 = <div className="mx-auto w-full max-w-[50rem] text-center">{t53}{t56}</div>;
    $[126] = t53;
    $[127] = t56;
    $[128] = t57;
  } else {
    t57 = $[128];
  }
  let t58;
  if ($[129] !== t) {
    t58 = <div className="mx-auto mt-8 max-w-[35.5rem] md:mt-16"><div className="flex flex-col gap-4">{Array.from({
          length: 5
        }, (__1, index_1) => {
          const itemIndex_0 = index_1 + 1 as NumericRange<1, 5>;
          return <div className="flex flex-row justify-between gap-4 align-top" key={itemIndex_0}><Text variant="p2" className="text-left">{t(`examples.items.${itemIndex_0}`)}</Text><div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-500/10"><IconCheck className="h-5 w-5 text-green-500" /></div></div>;
        })}</div></div>;
    $[129] = t;
    $[130] = t58;
  } else {
    t58 = $[130];
  }
  let t59;
  if ($[131] !== t57 || $[132] !== t58) {
    t59 = <AnimatedContent className="relative px-4">{t51}<div className="relative z-10">{t57}{t58}</div></AnimatedContent>;
    $[131] = t57;
    $[132] = t58;
    $[133] = t59;
  } else {
    t59 = $[133];
  }
  let t60;
  if ($[134] !== t50 || $[135] !== t59) {
    t60 = <AnimatedSection bgColor="bg-blue-50" id={t42}>{t50}{t59}</AnimatedSection>;
    $[134] = t50;
    $[135] = t59;
    $[136] = t60;
  } else {
    t60 = $[136];
  }
  let t61;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = getSectionAnchorId("csob");
    $[137] = t61;
  } else {
    t61 = $[137];
  }
  let t62;
  if ($[138] !== t) {
    t62 = t("csob.alt");
    $[138] = t;
    $[139] = t62;
  } else {
    t62 = $[139];
  }
  let t63;
  if ($[140] !== t62) {
    t63 = <div className="w-full shrink"><Image src="/images/sections/insuranceAndWarranty/cs/csob.png" alt={t62} width={600} height={616} className="md:max-w-fullg mx-auto max-w-[90%]" /></div>;
    $[140] = t62;
    $[141] = t63;
  } else {
    t63 = $[141];
  }
  let t64;
  if ($[142] === Symbol.for("react.memo_cache_sentinel")) {
    t64 = <Link className="text-blue-500! no-underline! decoration-0" to="https://www.csob.cz/csob/o-csob-a-skupine" testId="csob" analyticsParam="csob" />;
    $[142] = t64;
  } else {
    t64 = $[142];
  }
  let t65;
  if ($[143] !== t) {
    t65 = <SectionHeadline className="mb-5 lg:text-left" anchorKey="csob.title"><Trans t={t} i18nKey="csob.title">{t64}</Trans></SectionHeadline>;
    $[143] = t;
    $[144] = t65;
  } else {
    t65 = $[144];
  }
  let t66;
  if ($[145] !== t) {
    t66 = t("csob.text");
    $[145] = t;
    $[146] = t66;
  } else {
    t66 = $[146];
  }
  let t67;
  if ($[147] !== t66) {
    t67 = <Text variant="p2" className="text-center text-gray-800 lg:text-left">{t66}</Text>;
    $[147] = t66;
    $[148] = t67;
  } else {
    t67 = $[148];
  }
  let t68;
  if ($[149] !== t65 || $[150] !== t67) {
    t68 = <div className="w-[29.25rem] max-w-full shrink-0">{t65}{t67}</div>;
    $[149] = t65;
    $[150] = t67;
    $[151] = t68;
  } else {
    t68 = $[151];
  }
  let t69;
  if ($[152] !== t63 || $[153] !== t68) {
    t69 = <AnimatedContent className="relative z-10 px-4 pb-10 lg:pb-20"><ContentContainer><div className="flex flex-col items-center gap-12 lg:flex-row lg:gap-10">{t63}{t68}</div></ContentContainer></AnimatedContent>;
    $[152] = t63;
    $[153] = t68;
    $[154] = t69;
  } else {
    t69 = $[154];
  }
  let t70;
  if ($[155] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = LINKS().faqCategory("pojisteni-elektroniky");
    $[155] = t70;
  } else {
    t70 = $[155];
  }
  let t71;
  if ($[156] === Symbol.for("react.memo_cache_sentinel")) {
    t71 = <Link to={LINKS().faqCategory("pojisteni-elektroniky")} analyticsParam="faqInsurance" testId="faqInsurance" />;
    $[156] = t71;
  } else {
    t71 = $[156];
  }
  let t72;
  if ($[157] !== t) {
    t72 = <AnimatedContent className="py-10 lg:py-20"><FaqIntro link={t70} text={<Trans i18nKey="faq.text" t={t}>{t71}</Trans>} /></AnimatedContent>;
    $[157] = t;
    $[158] = t72;
  } else {
    t72 = $[158];
  }
  let t73;
  if ($[159] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = <IconDeferredPayment className="fillButtonIcon" />;
    $[159] = t73;
  } else {
    t73 = $[159];
  }
  let t74;
  if ($[160] !== t) {
    t74 = t("core:link.deferredPayment");
    $[160] = t;
    $[161] = t74;
  } else {
    t74 = $[161];
  }
  let t75;
  if ($[162] === Symbol.for("react.memo_cache_sentinel")) {
    t75 = LINKS();
    $[162] = t75;
  } else {
    t75 = $[162];
  }
  let t76;
  if ($[163] !== t74) {
    t76 = {
      analyticsParam: "InsuranceAndWarrantyMoreInfoDeferredPayment",
      icon: t73,
      testId: "insuranceAndWarrantyMoreInfoDeferredPayment",
      title: t74,
      to: t75.deferredPayment
    };
    $[163] = t74;
    $[164] = t76;
  } else {
    t76 = $[164];
  }
  let t77;
  if ($[165] === Symbol.for("react.memo_cache_sentinel")) {
    t77 = <IconMaxi className="fillButtonIcon" />;
    $[165] = t77;
  } else {
    t77 = $[165];
  }
  let t78;
  if ($[166] !== t) {
    t78 = t("core:products.revolving");
    $[166] = t;
    $[167] = t78;
  } else {
    t78 = $[167];
  }
  let t79;
  if ($[168] === Symbol.for("react.memo_cache_sentinel")) {
    t79 = LINKS();
    $[168] = t79;
  } else {
    t79 = $[168];
  }
  let t80;
  if ($[169] !== t78) {
    t80 = {
      analyticsParam: "InsuranceAndWarrantyMoreInfoRevolvingApplication",
      icon: t77,
      testId: "insuranceAndWarrantyMoreInfoRevolvingApplication",
      title: t78,
      to: t79.revolving
    };
    $[169] = t78;
    $[170] = t80;
  } else {
    t80 = $[170];
  }
  let t81;
  if ($[171] === Symbol.for("react.memo_cache_sentinel")) {
    t81 = <IconThird className="fillButtonIcon" />;
    $[171] = t81;
  } else {
    t81 = $[171];
  }
  let t82;
  if ($[172] !== t) {
    t82 = t("core:products.payInThree");
    $[172] = t;
    $[173] = t82;
  } else {
    t82 = $[173];
  }
  let t83;
  if ($[174] === Symbol.for("react.memo_cache_sentinel")) {
    t83 = LINKS();
    $[174] = t83;
  } else {
    t83 = $[174];
  }
  let t84;
  if ($[175] !== t82) {
    t84 = {
      analyticsParam: "InsuranceAndWarrantyMoreInfoPayInThree",
      icon: t81,
      testId: "insuranceAndWarrantyMoreInfoPayInThree",
      title: t82,
      to: t83.payInThree
    };
    $[175] = t82;
    $[176] = t84;
  } else {
    t84 = $[176];
  }
  let t85;
  if ($[177] === Symbol.for("react.memo_cache_sentinel")) {
    t85 = <IconCard className="fillButtonIcon" />;
    $[177] = t85;
  } else {
    t85 = $[177];
  }
  let t86;
  if ($[178] !== t) {
    t86 = t("core:products.card");
    $[178] = t;
    $[179] = t86;
  } else {
    t86 = $[179];
  }
  let t87;
  if ($[180] === Symbol.for("react.memo_cache_sentinel")) {
    t87 = LINKS();
    $[180] = t87;
  } else {
    t87 = $[180];
  }
  let t88;
  if ($[181] !== t86) {
    t88 = {
      analyticsParam: "InsuranceAndWarrantyMoreInfoCard",
      icon: t85,
      testId: "insuranceAndWarrantyMoreInfoCard",
      title: t86,
      to: t87.card
    };
    $[181] = t86;
    $[182] = t88;
  } else {
    t88 = $[182];
  }
  let t89;
  if ($[183] !== t76 || $[184] !== t80 || $[185] !== t84 || $[186] !== t88) {
    t89 = <AnimatedContent><MoreInfoSection buttons={[t76, t80, t84, t88]} /></AnimatedContent>;
    $[183] = t76;
    $[184] = t80;
    $[185] = t84;
    $[186] = t88;
    $[187] = t89;
  } else {
    t89 = $[187];
  }
  let t90;
  if ($[188] === Symbol.for("react.memo_cache_sentinel")) {
    t90 = <div className="bgCircle absolute right-0 bottom-0 z-[-1] translate-x-1/4 translate-y-1/4 rotate-180" />;
    $[188] = t90;
  } else {
    t90 = $[188];
  }
  let t91;
  if ($[189] !== t69 || $[190] !== t72 || $[191] !== t89) {
    t91 = <AnimatedSection bgColor="bg-blue-100" className="overflow-hidden" id={t61}>{t69}{t72}{t89}{t90}</AnimatedSection>;
    $[189] = t69;
    $[190] = t72;
    $[191] = t89;
    $[192] = t91;
  } else {
    t91 = $[192];
  }
  let t92;
  if ($[193] !== menuPromoBanners || $[194] !== t13 || $[195] !== t34 || $[196] !== t4 || $[197] !== t41 || $[198] !== t60 || $[199] !== t91) {
    t92 = <Layout menuPromoBanners={menuPromoBanners}>{t4}{t13}{t34}{t36}{t41}{t60}{t91}</Layout>;
    $[193] = menuPromoBanners;
    $[194] = t13;
    $[195] = t34;
    $[196] = t4;
    $[197] = t41;
    $[198] = t60;
    $[199] = t91;
    $[200] = t92;
  } else {
    t92 = $[200];
  }
  return t92;
}
export const getStaticProps = (async ctx => {
  const isAllowed = isAllowedRoute('pojisteni');
  if (!isAllowed) {
    return {
      notFound: true
    };
  }
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      pageBanner: await getPageBannerBySlug('insurance-and-warranty'),
      ...(await getIntlProps(ctx, ['core', 'insuranceAndWarranty']))
    }
  };
}) satisfies GetStaticProps<Props>;